import { gql } from '@apollo/client';

// Fetched separately and gated by `isAttendanceRequested` so we don't pay the
// wire cost (or the loader hop) on sessions where RSVPs are disabled. Server
// returns `[]` when the flag is off, but skipping the round-trip entirely is
// cleaner than relying on the short-circuit. Trade-off: when the flag is on,
// this is a second round-trip after the main query resolves.
export const GET_SESSION_ATTENDANCE_RESPONSES = gql`
  query GetSessionAttendanceResponses($_id: ID!) {
    assessmentSession(_id: $_id) {
      _id
      attendanceResponses {
        _id
        playerAssessmentId
        status
        reason
        reasonNote
      }
    }
  }
`;

// Token-authenticated public read: powers the public edit-player attendance
// table. Returns every upcoming ice time for the player behind the access
// token, each with that player's current RSVP status. After a recorded RSVP,
// the table component re-pulls this query via `refetchQueries` — each row's
// `status` is a server-side join of session + response, not an entity Apollo
// can normalize by id, so a cache write isn't enough.
export const GET_PLAYER_UPCOMING_ATTENDANCE = gql`
  query GetPlayerUpcomingAttendance($playerAssessmentId: ID!, $token: String) {
    playerUpcomingAttendance(playerAssessmentId: $playerAssessmentId, token: $token) {
      assessmentSessionId
      date
      start
      duration
      location
      area
      address
      isAttendanceRequested
      status
      reason
      reasonNote
      respondedByName
    }
  }
`;

// The one ice time an RSVP deep-link points at. No time filter, so an ice time
// that already started still resolves; null when the athlete isn't rostered on it.
export const GET_PLAYER_ATTENDANCE_SESSION = gql`
  query GetPlayerAttendanceSession($playerAssessmentId: ID!, $assessmentSessionId: ID!, $token: String) {
    playerAttendanceSession(playerAssessmentId: $playerAssessmentId, assessmentSessionId: $assessmentSessionId, token: $token) {
      date
      start
      duration
      location
      area
      address
    }
  }
`;

// Token-authenticated public read: identifies the player, the organization and
// (on the token path) the contact the link was sent to, for the page header and
// the "signed in as someone else" banner. Fired in parallel with
// `GetPlayerUpcomingAttendance` from the page shell — same token credential,
// so the two queries share the auth round-trip on the server. Kept separate
// from the upcoming-sessions query (rather than folded into it) because the
// header is loaded once per page mount and the sessions query refetches after
// every RSVP submission — bundling would re-fetch the header for nothing.
export const GET_PUBLIC_PLAYER_HEADER = gql`
  query GetPublicPlayerHeader($playerAssessmentId: ID!, $token: String) {
    publicPlayerHeader(playerAssessmentId: $playerAssessmentId, token: $token) {
      firstName
      lastName
      nickname
      dateOfBirth
      handedness
      positionName
      ageGroupName
      assessmentName
      heightCm
      weightKg
      organizationName
      organizationLogoUrl
      credentialContact {
        name
        email
        phone
        isVerifiedBySignedInParent
        canAddEmail
        canAddPhone
      }
    }
  }
`;

// Token-authenticated public RSVP submission
export const SUBMIT_ATTENDANCE_RESPONSE = gql`
  mutation SubmitAttendanceResponse($_id: ID!, $playerAssessmentId: ID!, $status: AttendanceStatus!, $reason: AttendanceReason, $reasonNote: String, $token: String) {
    submitAttendanceResponse(_id: $_id, playerAssessmentId: $playerAssessmentId, status: $status, reason: $reason, reasonNote: $reasonNote, token: $token) {
      _id
      playerAssessmentId
      status
      reason
      reasonNote
    }
  }
`;
